import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { SiteMapSurfaceType, SurfaceType } from "../DataAndMap";
import {
  processFeatureCollection,
  produceFeatureCollection,
} from "../utils/data";
import {
  Address,
  JobFeatureCollection,
  SeasonName,
} from "../../../services/axios/jobs";
import { useEditState } from "./EditStateContext";
import { updateHistory } from "../utils/history";
import { useParams } from "react-router-dom";

export interface ArrowData {
  id: number;
  coords: number[][];
  color: string;
}

export interface BoundaryData {
  id: number;
  coords: number[][];
  color: string;
}

export type HazardMarkerType = {
  id: number;
  lat: number;
  lng: number;
  color: string;
};

export type LabelMarkerType = {
  id: number;
  lat: number;
  lng: number;
  text: string;
  color: string;
  size: "sm" | "md" | "lg";
};

type FeatureVisibility = {
  value: string;
  label: string;
  visible: boolean;
};
export type SurfacesVisibility = {
  surfaceFeatures: boolean;
  hazardMarkers: boolean;
  labelMarkers: boolean;
  arrows: boolean;
  boundary: boolean;
  features: FeatureVisibility[];
};

export type DataContextType = {
  boundary?: BoundaryData;
  setBoundary: React.Dispatch<React.SetStateAction<BoundaryData | undefined>>;

  surfaceTypes: SurfaceType[];
  surfaceFeatures: SiteMapSurfaceType[];
  addSurfaceFeature: (feature: SiteMapSurfaceType) => void;
  deleteSurfaceFeature: (feature: SiteMapSurfaceType) => void;
  updateSurfaceFeature: (feature: SiteMapSurfaceType) => void;
  hazardMarkers: HazardMarkerType[];
  addHazardMarker: (marker: HazardMarkerType) => void;
  deleteHazardMarker: (marker: HazardMarkerType) => void;
  updateHazardMarker: (marker: HazardMarkerType) => void;
  labelMarkers: LabelMarkerType[];
  addLabelMarker: (marker: LabelMarkerType) => void;
  deleteLabelMarker: (marker: LabelMarkerType) => void;
  updateLabelMarker: (marker: LabelMarkerType) => void;
  arrows: ArrowData[];
  addArrow: (arrow: ArrowData) => void;
  deleteArrow: (arrow: ArrowData) => void;
  updateArrow: (arrow: ArrowData) => void;

  surfacesVisibility: SurfacesVisibility;
  setSurfacesVisibility: React.Dispatch<
    React.SetStateAction<SurfacesVisibility>
  >;
  setAllSurfacesVisibility: (visible: boolean) => void;

  propertyName?: string;
  propertyAddress?: Address;

  resetMapData: () => void;
  saveMapData: () => void;
  undoMapChange: () => void;
  disableUi?: boolean;
  isEmptyMap: boolean;
};

// Create the context
const DataContext = createContext<DataContextType | undefined>(undefined);

// Custom hook to use the context
export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

// Provider component
interface DataProviderProps {
  children: React.ReactNode;
  featureCollection?: JobFeatureCollection;
  surfaceTypes: SurfaceType[];
  surfacesVisibility?: SurfacesVisibility; // Surfaces (i.e. parking lots) to show on render.  Used by the backend to render site maps as images.
  onDelete: () => void;
  onSave: (featureCollection: JobFeatureCollection) => void;
  onAutoSave: (featureCollection: JobFeatureCollection) => Promise<void>;
  disableUi?: boolean;
  propertyAddress?: Address;
  propertyName?: string;
}

const useDirtyableState = <T,>(
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(initialValue);
  const { setIsDirty } = useEditState();

  const dirtySetter = (newValue: T | ((prevValue: T) => T)) => {
    setValue(newValue);
    setIsDirty(true);
  };

  return [value, dirtySetter];
};

export function buildDefaultSurfacesVisibility(
  surfaceTypes: SurfaceType[],
): SurfacesVisibility {
  return {
    surfaceFeatures: true,
    hazardMarkers: true,
    labelMarkers: true,
    arrows: true,
    boundary: true,
    features: surfaceTypes.map((s) => ({
      value: s.value,
      label: s.label,
      visible: true,
    })),
  };
}

export const DataProvider: React.FC<DataProviderProps> = ({
  children,
  featureCollection = undefined,
  surfaceTypes,
  surfacesVisibility,
  onDelete,
  onSave,
  onAutoSave,
  disableUi,
  propertyAddress,
  propertyName,
}) => {
  const { jobId, season } = useParams<{ season: SeasonName; jobId: string }>();
  const { boundary, surfaceFeatures, hazardMarkers, labelMarkers, arrows } =
    processFeatureCollection(featureCollection);

  const [boundaryState, setBoundaryState] = useDirtyableState<
    BoundaryData | undefined
  >(boundary);
  const [surfaceFeaturesState, setSurfaceFeaturesState] =
    useDirtyableState<SiteMapSurfaceType[]>(surfaceFeatures);
  const [hazardMarkersState, setHazardMarkersState] =
    useDirtyableState<HazardMarkerType[]>(hazardMarkers);
  const [labelMarkersState, setLabelMarkersState] =
    useDirtyableState<LabelMarkerType[]>(labelMarkers);
  const [arrowsState, setArrowsState] = useDirtyableState<ArrowData[]>(arrows);

  const DefaultSurfacesVisibility =
    buildDefaultSurfacesVisibility(surfaceTypes);
  const [internalSurfacesVisibility, setSurfacesVisibility] =
    useState<SurfacesVisibility>(
      surfacesVisibility ?? DefaultSurfacesVisibility,
    );

  useEffect(() => {
    const geoJson = produceFeatureCollection(
      surfaceFeaturesState,
      hazardMarkersState,
      labelMarkersState,
      arrowsState,
      boundaryState,
    );
    onAutoSave(geoJson);
  }, [
    arrowsState,
    boundaryState,
    hazardMarkersState,
    labelMarkersState,
    onAutoSave,
    surfaceFeaturesState,
  ]);

  const undoMapChange = useCallback(() => {
    updateHistory<JobFeatureCollection>((h) => {
      const removedItem = h.undo();
      if (removedItem) {
        const newGeoJson = h.current()?.item ?? removedItem.item;
        const processed = processFeatureCollection(newGeoJson);
        setSurfaceFeaturesState(processed.surfaceFeatures);
        setHazardMarkersState(processed.hazardMarkers);
        setLabelMarkersState(processed.labelMarkers);
        setArrowsState(processed.arrows);
      } else {
        alert("No more changes to undo");
      }
    }, `site-map-history-${jobId}-${season}`);
  }, [
    jobId,
    season,
    setArrowsState,
    setHazardMarkersState,
    setLabelMarkersState,
    setSurfaceFeaturesState,
  ]);

  const addSurfaceFeature = (feature: SiteMapSurfaceType) => {
    // console.log('adding feature', feature);
    setSurfaceFeaturesState((prevFeatures) => [...prevFeatures, feature]);
  };
  const deleteSurfaceFeature = (feature: SiteMapSurfaceType) => {
    setSurfaceFeaturesState((prevFeatures) =>
      prevFeatures.filter((f) => f.id !== feature.id),
    );
    onDelete();
  };
  const updateSurfaceFeature = (feature: SiteMapSurfaceType) => {
    setSurfaceFeaturesState((prevFeatures) =>
      prevFeatures.map((f) => (f.id === feature.id ? feature : f)),
    );
  };

  const addArrow = (arrow: ArrowData) => {
    setArrowsState((prevArrows) => [...prevArrows, arrow]);
  };
  const deleteArrow = (arrow: ArrowData) => {
    setArrowsState((prevArrows) => prevArrows.filter((a) => a.id !== arrow.id));
    onDelete();
  };
  const updateArrow = useCallback(
    (arrow: ArrowData) => {
      setArrowsState((prevArrows) =>
        prevArrows.map((a) => (a.id === arrow.id ? arrow : a)),
      );
    },
    [setArrowsState],
  );

  const addLabelMarker = (marker: LabelMarkerType) => {
    setLabelMarkersState((prevMarkers) => [...prevMarkers, marker]);
  };
  const deleteLabelMarker = (marker: LabelMarkerType) => {
    setLabelMarkersState((prevMarkers) =>
      prevMarkers.filter((m) => m.id !== marker.id),
    );
    onDelete();
  };
  const updateLabelMarker = (marker: LabelMarkerType) => {
    setLabelMarkersState((prevMarkers) =>
      prevMarkers.map((m) => (m.id === marker.id ? marker : m)),
    );
  };

  const addHazardMarker = (marker: HazardMarkerType) => {
    setHazardMarkersState((prevMarkers) => [...prevMarkers, marker]);
  };
  const deleteHazardMarker = (marker: HazardMarkerType) => {
    setHazardMarkersState((prevMarkers) =>
      prevMarkers.filter((m) => m.id !== marker.id),
    );
    onDelete();
  };
  const updateHazardMarker = (marker: HazardMarkerType) => {
    setHazardMarkersState((prevMarkers) =>
      prevMarkers.map((m) => (m.id === marker.id ? marker : m)),
    );
  };

  const resetMapData = () => {
    const { surfaceFeatures, hazardMarkers, labelMarkers, arrows } =
      processFeatureCollection(featureCollection);

    setSurfaceFeaturesState(surfaceFeatures);
    setHazardMarkersState(hazardMarkers);
    setLabelMarkersState(labelMarkers);
    setArrowsState(arrows);
  };

  const saveMapData = () => {
    // console.log("saving map data");
    // if (!boundaryState) {
    //   console.error(
    //     "Somehow ended up trying to save a site map without a boundary.",
    //   );
    //   return;
    // }
    const newFeatureCollection = produceFeatureCollection(
      surfaceFeaturesState,
      hazardMarkersState,
      labelMarkersState,
      arrowsState,
      boundaryState,
    );

    return onSave(newFeatureCollection);
  };

  const setAllSurfacesVisibility = useCallback(
    (visible: boolean) => {
      setSurfacesVisibility({
        surfaceFeatures: visible,
        hazardMarkers: visible,
        labelMarkers: visible,
        arrows: visible,
        boundary: visible,
        features: surfaceTypes.map((s) => ({
          value: s.value,
          label: s.label,
          visible,
        })),
      });
    },
    [surfaceTypes],
  );

  const value: DataContextType = {
    boundary: internalSurfacesVisibility.boundary ? boundaryState : undefined,
    setBoundary: setBoundaryState,

    surfaceFeatures: surfaceFeaturesState,
    surfaceTypes,
    addSurfaceFeature: addSurfaceFeature,
    deleteSurfaceFeature: deleteSurfaceFeature,
    updateSurfaceFeature: updateSurfaceFeature,
    hazardMarkers: internalSurfacesVisibility.hazardMarkers
      ? hazardMarkersState
      : [],
    addHazardMarker: addHazardMarker,
    deleteHazardMarker: deleteHazardMarker,
    updateHazardMarker: updateHazardMarker,
    labelMarkers: internalSurfacesVisibility.labelMarkers
      ? labelMarkersState
      : [],
    addLabelMarker: addLabelMarker,
    deleteLabelMarker: deleteLabelMarker,
    updateLabelMarker: updateLabelMarker,
    arrows: internalSurfacesVisibility.arrows ? arrowsState : [],
    addArrow: addArrow,
    deleteArrow: deleteArrow,
    updateArrow: updateArrow,

    surfacesVisibility: internalSurfacesVisibility,
    setSurfacesVisibility,
    setAllSurfacesVisibility,

    propertyName,
    propertyAddress,

    resetMapData,
    saveMapData,
    undoMapChange,

    disableUi,
    isEmptyMap: false, // overwritten below
  };
  value.isEmptyMap =
    value.hazardMarkers.length < 1 &&
    value.labelMarkers.length < 1 &&
    value.arrows.length < 1 &&
    value.boundary == null &&
    value.surfaceFeatures.length < 1;

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataProvider;
